import React, { useState, useEffect } from "react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { useNavigate, useParams } from "react-router-dom";
import axios, { cookies, MEDIA_BASE_URL } from "../axios/axios";
import { ToastContainer, toast } from "react-toastify";

const VideoEditingForm = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [file, setFile] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [loading, setLoading] = useState(false);

  const { id } = useParams();
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get("/services/getServices");
        setServices(response.data.services);
      } catch (error) {
        toast.error(error.response?.data?.message || "An error occurred");
      }
    };
    fetchServices();
  }, []);

  const newData = services.find(
    (val) => val.id.replaceAll(" ", "-").replaceAll("/", "_") === id
  );

  const [data, setData] = useState({
    price: "",
    unit_price: "",
    order_name: "",
    video_package: [],
    raw_footage: "",
    output_video: "",
    color_correction: "",
    music: "",
    quantity: "",
    social_media: "",
    drive_link: "",
    additional_notes: "",
  });

  useEffect(() => {
    if (newData) {
      setData((prevData) => ({
        ...prevData,
        unit_price: Number(newData.price) || 0,
        price: Number(newData.price) || 0,
        quantity: prevData.quantity || "1",
      }));
    }
  }, [newData]);

  const packagePrices = {
    "Full length video": 249,
    Highlight: 99,
    Teaser: 40,
    Reel: 35,
  };

  const getQuantity = () => Math.max(0, Number(data.quantity) || 0);

  const computeUnitPrice = (packages) => {
    const base = Number(newData?.price) || 0;
    const additions = (packages || []).reduce(
      (s, pkg) => s + (packagePrices[pkg] || 0),
      0
    );
    return Number(base + additions);
  };

  const computeTotalPrice = (unit_price, qty, discountPercent = 0) => {
    const base = Number(unit_price || 0) * Math.max(1, qty || 1);
    if (discountPercent) {
      return Number((base * (1 - discountPercent / 100)).toFixed(2));
    }
    return Number(base.toFixed(2));
  };

  const dataHandler = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox" && name === "video_package") {
      setData((prevData) => {
        const updatedPackages = checked
          ? [...prevData.video_package, value]
          : prevData.video_package.filter((pkg) => pkg !== value);

        const unit = computeUnitPrice(updatedPackages);
        const qty = getQuantity();
        const total = computeTotalPrice(
          unit,
          qty,
          isCouponApplied ? discount : 0
        );

        return {
          ...prevData,
          video_package: updatedPackages,
          unit_price: unit,
          price: total,
        };
      });
    } else if (name === "quantity") {
      const qty = Math.max(0, Math.floor(Number(value) || 0));

      setData((prevData) => {
        const unit =
          Number(prevData.unit_price) ||
          computeUnitPrice(prevData.video_package);
        const total = computeTotalPrice(
          unit,
          qty,
          isCouponApplied ? discount : 0
        );
        return { ...prevData, quantity: qty.toString(), price: total };
      });

      // Enforce file count trimming if needed
      if (file && file.length > 0) {
        const selectedCount = file.length;
        if (qty === 1 && selectedCount > 1) {
          setFile([file[0]]);
          toast.info("Quantity set to 1 — extra selected files were removed.");
        } else if (qty > 0 && selectedCount > qty) {
          setFile(file.slice(0, qty));
          toast.info(
            `Quantity set to ${qty} — extra selected files were removed.`
          );
        }
      }
    } else {
      setData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const onFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const qty = getQuantity();

    if (qty > 0) {
      if (selectedFiles.length > qty) {
        const trimmed = selectedFiles.slice(0, qty);
        setFile(trimmed);
        toast.warn(
          `You selected more than ${qty} files — only the first ${qty} were kept.`
        );
        return;
      }

      if (selectedFiles.length < qty) {
        setFile(selectedFiles);
        toast.info(
          `You selected ${selectedFiles.length} files. Please select exactly ${qty} files before submitting.`
        );
        return;
      }

      setFile(selectedFiles);
    } else {
      setFile(selectedFiles);
      toast.info("Set quantity to enforce exact file count.");
    }
  };

  const applyCoupon = async () => {
    try {
      const storedUser = cookies.get("user");
      const userId = storedUser?.user?.id || null;

      if (!userId) {
        toast.error("User not found. Please log in again.");
        return;
      }

      const response = await axios.post("/coupon/validate", {
        code: couponCode,
        userId,
      });

      if (response.data.valid) {
        setDiscount(response.data.discount);
        setIsCouponApplied(true);

        setData((prevData) => {
          const unit =
            Number(prevData.unit_price) ||
            computeUnitPrice(prevData.video_package);
          const qty = getQuantity() || 1;
          const total = computeTotalPrice(unit, qty, response.data.discount);
          return { ...prevData, price: total };
        });

        toast.success("Coupon applied!");
      } else {
        setErrorMessage(response.data.message || "Coupon not valid");
        toast.error(response.data.message || "Coupon not valid");
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      setErrorMessage(
        error.response?.data?.message || "Error validating coupon."
      );
      toast.error(error.response?.data?.message || "An error occurred");
    }
  };

  const dataSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: responseData } = await axios.post(
        `/video-editing/${id}`,
        data
      );

      toast.success("Data Submitted Successfully!");
      navigate(`/upload-files/${responseData.id}?type=video-editing`);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "An error occurred");
      toast.error(error.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (!newData) {
    return <h2 className="text-center my-5">Loading ...</h2>;
  }

  const qty = getQuantity();
  const originalTotal = Number(data.unit_price || 0) * (qty || 1);

  return (
    <>
      <Navbar />
      <div className="mx-5">
        <div className="row align-items-center py-5 ">
          <div className="col-md-6 my-4">
            <img
              className="w-100 object-fit-cover"
              height={600}
              src={`${MEDIA_BASE_URL}/${newData.image}`}
              crossOrigin="anonymous"
              alt=""
            />
          </div>
          <div className="col-md-6 my-4 ps-5">
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="colored"
            />
            <h4 className="mb-3">{newData.title}</h4>

            <h5 className="mb-3">
              Price:{" "}
              {isCouponApplied ? (
                <>
                  <del className="text-danger me-2">
                    ${originalTotal.toFixed(2)}
                  </del>
                  <span className="text-success">
                    ${Number(data.price).toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-dark">
                  ${Number(data.price).toFixed(2)}
                </span>
              )}
            </h5>

            {/* Coupon Code Input */}
            <div className="row  g-3 align-items-center mb-1">
              <div className="col-md-4">
                <label className="fs-14 col-form-label">Coupon Code</label>
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control shadow-none rounded-2 p-2"
                  placeholder="Enter Coupon"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  disabled={isCouponApplied}
                />
              </div>
              <button
                className="col-md-2 btn py-2 px-0 rounded-2 btn-secondary mt-2"
                onClick={applyCoupon}
                disabled={isCouponApplied}
              >
                Apply
              </button>
            </div>

            <form onSubmit={dataSubmit}>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">Order Name</label>
                </div>
                <div className="col-md-8">
                  <input
                    onChange={(e) =>
                      setData((prevData) => ({
                        ...prevData,
                        order_name: e.target.value,
                      }))
                    }
                    name="order_name"
                    value={data.order_name}
                    type="text"
                    className="form-control shadow-none rounded-2 p-2"
                  />
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">Video Type</label>
                </div>
                <div className="col-md-8">
                  <div className="w-100 p-2">
                    <label className="d-block">
                      <input
                        type="checkbox"
                        name="video_package"
                        value="Full length video"
                        checked={data.video_package.includes(
                          "Full length video"
                        )}
                        onChange={dataHandler}
                      />
                      Full Length Video $249
                    </label>

                    <label className="d-block">
                      <input
                        type="checkbox"
                        name="video_package"
                        value="Highlight"
                        checked={data.video_package.includes("Highlight")}
                        onChange={dataHandler}
                      />
                      Highlight $99
                    </label>

                    <label className="d-block">
                      <input
                        type="checkbox"
                        name="video_package"
                        value="Teaser"
                        checked={data.video_package.includes("Teaser")}
                        onChange={dataHandler}
                      />
                      Teaser $40
                    </label>

                    <label className="d-block">
                      <input
                        type="checkbox"
                        name="video_package"
                        value="Reel"
                        checked={data.video_package.includes("Reel")}
                        onChange={dataHandler}
                      />
                      Reels $35
                    </label>
                  </div>
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">Raw Footage</label>
                </div>
                <div className="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="raw_footage"
                    value={data.raw_footage}
                    className="w-100 p-2 form-select"
                  >
                    <option value="">Select</option>
                    <option value="Upto 1 hour">Upto 1 hour</option>
                    <option value="Upto 2 hour">Upto 2 hour</option>
                    <option value="Upto 3 hour">Upto 3 hour</option>
                    <option value="Upto 3-5 hour">Upto 3-5 hour</option>
                    <option value="Upto 8-10 hour">Upto 8-10 hour</option>
                    <option value="Upto 10-12 hour">Upto 10-12 hour</option>
                  </select>{" "}
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">Output Video</label>
                </div>
                <div className="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="output_video"
                    value={data.output_video}
                    className="w-100 p-2 form-select"
                  >
                    <option value="">Select</option>
                    <option value="Less than 5 minutes">
                      Less than 5 minutes
                    </option>
                    <option value="5-15 minutes">5-15 minutes</option>
                    <option value="16-30 minutes">16-30 minutes</option>
                    <option value="30-45 minutes">30-45 minutes</option>
                    <option value="46-60 minutes">46-60 minutes</option>
                  </select>{" "}
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">
                    Color Correction
                  </label>
                </div>
                <div className="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="color_correction"
                    value={data.color_correction}
                    className="w-100 p-2 form-select"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>{" "}
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">Music</label>
                </div>
                <div className="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="music"
                    value={data.music}
                    className="w-100 p-2 form-select"
                  >
                    <option value="">Select</option>
                    <option value="My music">My music</option>
                    <option value="Choose for me">Choose for me</option>
                  </select>{" "}
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">Social Media</label>
                </div>
                <div className="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="social_media"
                    value={data.social_media}
                    className="w-100 p-2 form-control"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>{" "}
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">Quantity</label>
                </div>
                <div className="col-md-8">
                  <input
                    type="number"
                    onChange={dataHandler}
                    name="quantity"
                    value={data.quantity}
                    className="form-control shadow-none rounded-2 p-2"
                    min={1}
                  />
                </div>
              </div>

              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">
                    Aditional notes
                  </label>
                </div>
                <div className="col-md-8">
                  <textarea
                    onChange={dataHandler}
                    name="additional_notes"
                    value={data.additional_notes}
                    className="form-control shadow-none rounded-2 p-2"
                  ></textarea>{" "}
                </div>
              </div>
              <button
                type="submit"
                className="btn bg_maincolor text-white"
                disabled={loading}
              >
                {loading ? "Loading..." : "Order Now"}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default VideoEditingForm;
